import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getSCAReportByAgent as getSCAReportByAgentService } from 'services/modules/soc/agent';
import Breadcrumbs from 'config/Breadcrumb';

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningIcon from '@mui/icons-material/Warning';

import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TablePagination,
    Tooltip,
    Button,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
} from '@mui/material';
import { useSelector } from 'react-redux';

const SCAReport = () => {
    // useLocation hook
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const agentName = queryParams.get('agentname');
    const agentId = queryParams.get('agentid');
    const policyId = queryParams.get('policyid');
    const categoryIndex = location.state;
    const versionIndex = categoryIndex.lastIndexOf(' ');
    const policyCategory = versionIndex !== -1 ? categoryIndex.substring(0, versionIndex) : categoryIndex;

    // React router
    const navigate = useNavigate();

    // State Variable
    const [status, setStatus] = useState('all')
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPath, setCurrentPath] = useState(['Dashboard', 'SCA Report']);

    // Redux Selectors
    const selectedCompanyContainer = useSelector((state)=>state.auth.companyData)

    // Method handles the SCA Report data based on agent id, policy id and policy type
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const params = { agentid: agentId, policy_id: policyId, limit: rowsPerPage, offset: page * rowsPerPage, status: status,soc_env:selectedCompanyContainer?.soc_env }
                const response = await getSCAReportByAgentService({ params });
                setData(response?.affected_items);
                setTotalCount(response?.total_affected_items);
                setCurrentPath(['Dashboards', 'SCA Report', agentName]);

            } catch (error) {
                console.error("Error fetching threat hunting alerts:", error);
            } finally {
                setLoading(false);
            }
        };

        if (agentId && policyId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId, policyId, page, rowsPerPage, status]);

    // Handles page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handles change in rows per page
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Method to navigate to view sca report data component
    const viewAlerts = (alert) => {
        navigate('/soc/view-sca-report', { state: { data: alert, name: agentName } });
    };

    // Handles the class name based on the  policy result value
    const getResult = (value) => {
        if (value === 'passed') {
            return 'informational'
        }
        else if (value === 'failed') {
            return 'fail'
        }
        else if (value === 'not applicable') {
            return 'notapplicable'
        }
    }

    return (
        <div className='p-3'>
            <div className="d-flex justify-content-between ">
                <div className="pt-4">
                    <Breadcrumbs paths={currentPath} />
                </div>
                <div>
                    <Button
                        variant="contained"
                         className="backfont mt-3 form-card"
                        onClick={() => window.history.back()}
                    >
                        Back
                    </Button>
                </div>
            </div>
            <Typography variant="h5" align="center" > View SCA Report ({policyCategory})  </Typography>
            <div className=" col-lg-2 col-md-4 col-sm-12 col-xs-12 search-ecs">
                <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Status</InputLabel>

                    <Select
                        value={status}
                        label="Status"
                        onChange={(e) => {
                            setStatus(e.target.value)
                            setPage(0)
                        }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="passed">Passed</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="not applicable">Not Applicable</MenuItem>


                    </Select>
                </FormControl>
            </div>
            {loading ? (
                <div className='d-flex justify-content-center align-items-center mt-4' >
                    <CircularProgress />
                </div>
            ) : (
                <>

                    <TableContainer >
                        <Table >
                            <TableHead >
                                <TableRow >
                                    <TableCell align='center'>ID</TableCell>
                                    <TableCell align='center'>Policy Name</TableCell>
                                    <TableCell align='center'>Target</TableCell>
                                    <TableCell align='center'>Description</TableCell>
                                    <TableCell align='center'>Status</TableCell>
                                    <TableCell align='center'>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <WarningIcon color="disabled" fontSize="small" />
                                            No Data Available
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data.map((alert, index) => (
                                        <TableRow key={index}>
                                            <TableCell   align ='center'>{alert?.id}</TableCell>
                                            <TableCell className='text-secondary' style={{ wordBreak: 'break-word', maxWidth: '25%', whiteSpace: 'normal' }}
                                            >{alert?.title}</TableCell>
                                            <TableCell style={{ wordBreak: 'break-word', maxWidth: '25%', whiteSpace: 'normal' }}
                                            >
                                                {alert?.command ? (
                                                    <> <strong>Command:</strong> {alert.command}</>
                                                ) : alert?.registry ? (
                                                    <> <strong>Registry:</strong> {alert.registry}</>
                                                ) : alert?.file ? (
                                                    <> <strong>File:</strong> {alert.file}</>
                                                ) : alert?.process ? (
                                                    <> <strong>Process:</strong> {alert.process}</>
                                                ) : alert?.directory ? (
                                                    <> <strong>Directory:</strong> {alert.directory}</>
                                                ) : (
                                                    <span></span>
                                                )}
                                            </TableCell>
                                            <TableCell>{ alert.description &&  alert.description.length > 70 ? alert.description.slice(0,70)+'...' : alert.description}</TableCell>
                                            <TableCell align='center' > <span className={`${getResult(alert?.result)} p-2 m-0 d-block`} style={{ width: '90px' }}>{alert?.result?.charAt(0)?.toUpperCase() + alert?.result?.slice(1)}</span></TableCell>
                                            <TableCell align='center' >
                                                <Tooltip title="View" placement="top">
                                                    <Button>
                                                        <VisibilityOutlinedIcon sx={{ color: '#3d4985', fontSize: "25px", }} onClick={() => { viewAlerts(alert) }} />
                                                    </Button>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}

        </div>
    );

};

export default SCAReport;